import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AsyncSelect from "react-select/async";
import DOMPurify from "dompurify";
import moment from "moment";
import { useStoreState } from "src/pages/contentMap/index";
import { useDispatch } from "react-redux";
import Dropdown from "react-bootstrap/Dropdown";

import { TagsInput } from "src/components/tagsInput";
import DatePickerComponent from "src/components/datePicker";

import {
  invalidateAssetsSearchResults,
  invalidateCollectionsSearchResults,
  invalidateCompaniesSearchResults,
  invalidateContactSearchResults,
  invalidateStreamsSearchResults,
  invalidateUserGroupSearchResults,
  invalidateUserSearchResults,
  searchAssets,
  searchCollections,
  searchCompanies,
  searchContacts,
  searchDealRooms,
  searchGroups,
  searchStreams,
  searchUserGroupsForAutoComplete,
  searchUserRolesForAutoComplete,
  searchUsers,
} from "src/redux/actions/search";

import { AssetExpiry } from "src/types/clusterFilters";
import { CustomField, ICustomFieldOption } from "src/types/customFields";
import { Operators } from 'src/types/clusterFilters';
import TruncateWithTooltip from "src/components/truncateWithTooltip";

import { FilterContext, PrimaryFilterProps } from "../index";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";
import { CONTENT_EXPIRY_FILTERS, FILE_TYPES } from "src/constants/contentMap";
import { Stream } from "src/types/streams";
import { Group } from "src/types/groups";
import { Asset } from "src/types/assets";
import { Collection } from "src/types/collections";
import { User } from "src/types/streamAccessControl";
import { UserGroup } from "src/types/users";
import { Contact } from "src/types/contact";

import userRoleService from "src/utils/userRoles.service";

import _ from "lodash";
import styles from "./filterInput.module.scss";
import { hideInputField } from "src/services/reports.service";

const InputBox: React.FC<PrimaryFilterProps> = ({ filter }) => {
  const property = _.get(filter, "property.type", "");

  const canAccessCollections = userRoleService.canAccessCollection();
  const canAccessDealRooms = userRoleService.canAccessDealRoom();

  const canShare = userRoleService.canShare();

  switch (property) {
    case "title":
      return <AssetTitleSearch filter={filter} />;

      case "collection":
        return canAccessCollections ? <RenderSelect filter={filter} /> : null;

      case "dealRoom":
        return canAccessDealRooms ? <RenderSelect filter={filter} /> : null;

      case "contact":
      case "company":
        return canShare ? <RenderSelect filter={filter} /> : null;
  
      case "asset":
      case "stream":
      case "group":
      case "user":
      case "user_group":
        return <RenderSelect filter={filter} />;
    case "file_type":
    case "custom_field":
      return <DropdownComponent filter={filter} />;
    case "tags":
      return <RenderTagsInput filter={filter} />;

    case "expiryDate":
      return <ExpiryDateDropdown filter={filter} />;
  }

  return null;
};

const AssetTitleSearch: React.FC<PrimaryFilterProps> = ({ filter }) => {
  const { handleValueChange } = React.useContext(FilterContext);

  const [query, setQuery] = React.useState<string>("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  useEffect(() => {
    setQuery(_.get(filter, "value", ""));
  }, [filter]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleValueChange(_.get(filter, "id", ""), [query]);
  };

  return (
    <div className={styles.inputFieldContainer}>
      <form onSubmit={handleSubmit}>
        <input
          onChange={handleChange}
          value={query}
          type="text"
          placeholder="type something..."
        />
      </form>
    </div>
  );
};

const RenderSelect: React.FC<PrimaryFilterProps> = ({ filter }) => {
  const dispatch = useDispatch();
  const [selectedOptions, setSelectedOptions] = useState([]);

  const property = _.get(filter, "property.type", "");
  const operator = _.get(filter, "operator", "");

  const shouldHideInput = hideInputField(operator);

  useEffect(() => {
    const selectedValues = _.get(filter, "value", []);
    setSelectedOptions(selectedValues);
  }, [filter]);

  useEffect(() => {
    return () => {
      dispatch(invalidateAssetsSearchResults());
      dispatch(invalidateStreamsSearchResults());
      dispatch(invalidateCollectionsSearchResults());
      dispatch(invalidateUserSearchResults());
      dispatch(invalidateUserGroupSearchResults());
      dispatch(invalidateContactSearchResults());
      dispatch(invalidateCompaniesSearchResults());
    };
  }, []);

  const { handleValueChange } = React.useContext(FilterContext);

  let storePath;
  if (property === "company" || property === "contact") {
    storePath = `search.${property}`;
  } else if (property === "user_group") {
    storePath = `search.userGroups`;
  } else if (property === "user" && operator === "has_role") {
    storePath = `search.userRole`;
  } else {
    storePath = `search.${property}s`;
  }

  const { loading, succeeded, items, error } = useStoreState(storePath, true);
  useSelector((state) => _.get(state, "hub.settings.assets", {}));

  const labelField = property === "contact" ? "email" : "name";

  const getPropertyAction = () => {
    const canAccessCollection = userRoleService.canAccessCollection();
    const canShare = userRoleService.canShare();
    if (property === "collection" && canAccessCollection) {
      return searchCollections;
    }
  
    if (property === "contact" && canShare) {
      return searchContacts;
    }
  
    if (property === "company" && canShare) {
      return searchCompanies;
    }

    if (property === "user" && operator === "has_role") {
      return searchUserRolesForAutoComplete;
    }
    switch (property) {
      case "asset":
        return searchAssets;
      case "stream":
        return searchStreams;
      case "group":
        return searchGroups;
      case "collection":
        return searchCollections;
      case "dealRoom":
        return searchDealRooms;
      case "user":
        return searchUsers;
      case "user_group":
        return searchUserGroupsForAutoComplete;
      case "contact":
        return searchContacts;
      case "company":
        return searchCompanies;
      default:
        return searchAssets;
    }
  };

  const useFetchProps: IUseFetchProps = {
    action: getPropertyAction(),
    loading,
    succeeded,
    response: items,
    error,
  };

  const { handleFetch } = useFetch(useFetchProps);

  const getStyles = () => {
    return {
      control: (base: object) => ({
        ...base,
        minHeight: "1rem",
      }),
      indicatorSeparator: (base: object) => ({
        ...base,
        alignSelf: "auto",
      }),
      dropdownIndicator: (provided: object) => ({
        ...provided,
        display: "none",
      }),
    };
  };

  const handleChange = (options: any) => {
    const exitstingValues = _.map(selectedOptions, "id");
    if (_.includes(exitstingValues, options[0].item.id)) {
      return;
    }
    handleValueChange(_.get(filter, "id", ""), [
      ...selectedOptions,
      options[0].item,
    ]);
  };

  const loadOptions = (queryString) => {
    if (!queryString || queryString.trim() === "") {
      return Promise.resolve([]);
    }

    try {
      const requestBody = {
        query: queryString,
        filter: null,
        page: 0,
        limit: 15,
        highlight: false,
      };

      if (property === "asset") {
        requestBody["searchWithinHub"] = true;
      }

      return handleFetch(requestBody)?.then(
        (results: (Asset | Stream | Group | Collection | User | UserGroup | Contact)[]) => {
          const options = results.map((item) => {
            let labelText = _.get(item, labelField, "");
            if (property === "user" && operator !== "has_role") {
              const firstName = _.get(item, "firstName", "");
              const lastName = _.get(item, "lastName", "");
              labelText = `${firstName} ${lastName}`.trim();

              if (!_.get(item, "enabled", true)) {
                labelText += " (deactivated)";
              }
            }

            const disabledStyle: React.CSSProperties = !_.get(
              item,
              "enabled",
              true,
            )
              ? { color: "#A9A9A9" }
              : {};

            return {
              value: item.id,
              item: item,
              label: (
                <div
                  style={disabledStyle}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(labelText),
                  }}
                ></div>
              ),
            };
          });
          return options;
        },
      );
    } catch (e) {
      console.log("error", e);
    }
  };

  const handleDeleteClick = (item: Asset | Stream | Group) => {
    const updatedFilters = selectedOptions.filter((selectedItem) => {
      return _.get(selectedItem, "id", "") !== item.id;
    });

    handleValueChange(_.get(filter, "id", ""), updatedFilters);
  };

  if (shouldHideInput) {
    return null;
  }

  return (
    <div className={styles.selectContainer}>
      <AsyncSelect
        className={styles.searchInput}
        isMulti
        placeholder="type something..."
        filterOption={() => {
          return true;
        }}
        value={null}
        styles={getStyles()}
        loadOptions={loadOptions}
        onChange={handleChange}
        loadingMessage={() => `Loading...`}
        noOptionsMessage={() => `No results found`}
      />

      <RenderSelectedOptions
        selectedOptions={selectedOptions}
        handleDeleteClick={handleDeleteClick}
        property={property}
      />
    </div>
  );
};

const RenderTagsInput: React.FC<PrimaryFilterProps> = ({ filter }) => {
  const { handleValueChange } = React.useContext(FilterContext);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState<boolean>(false);

  const handleTagValuesUpdate = (updatedTags) => {
    handleValueChange(_.get(filter, "id", ""), updatedTags);
  };

  useEffect(() => {
    if (_.size(_.get(filter, "value", [])) > 0) {
      const extractedTags = _.get(filter, "value", []);
      setSelectedTags(extractedTags);
    }
    if (_.get(filter, "operator") === Operators.is_empty || _.get(filter, "operator") === Operators.is_not_empty) {
      setTagInput(false);
    } else {
      setTagInput(true);
    }
  }, [filter])

  if (!tagInput) {
    return <></>;
  }

  return (
    <div className={styles.tagsOuterContainer}>
      <TagsInput selectedTags={selectedTags} handleTagValuesUpdate={handleTagValuesUpdate} />
    </div>
  );
};

interface IOption {
  key: string;
  value: string;
  text: string;
}

const DropdownComponent: React.FC<PrimaryFilterProps> = ({ filter }) => {
  const property = _.get(filter, "property.type", "");
  const isCustomField = property === "custom_field";

  const { handleValueChange } = React.useContext(FilterContext);
  const [dropDownOptions, setDropDownOptions] = React.useState<(IOption | ICustomFieldOption)[]>([]);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const { items } = useStoreState("customFields", true);
  const activeCustomField = _.find(items, { id: _.get(filter, "property.fieldId") });

  useEffect(() => {
    if (property === "file_type") {
      setDropDownOptions(FILE_TYPES)
    }
    else if (property === "custom_field") {
      setDropDownOptions(activeCustomField?.options)
    }
    if ((_.get(filter, "operator") === Operators.is_empty) || (_.get(filter, "operator") === Operators.is_not_empty)) {
      setShowFilters(false);
    }
    else {
      setShowFilters(true);
    }
  }, [filter, activeCustomField]);

  const handleOptionSelect = (item: IOption | ICustomFieldOption) => {
    if (isCustomField) {
      const filterValues = _.get(filter, "value", []);
      if (_.includes(filterValues, _.get(item, "id", ""))) {
        return;
      }
      handleValueChange(_.get(filter, "id", ""), [...filterValues, _.get(item, "id")])
    }
    else {
      const filterValues = _.get(filter, "value", []);
      if (_.includes(filterValues, item.value)) {
        return;
      }
      handleValueChange(_.get(filter, "id", ""), [...filterValues, item.value])
    }
  }

  const selectedOptions = _.get(filter, "value", []);


  const handleDeleteClick = (item: IOption | ICustomFieldOption) => {
    const updatedFilters = selectedOptions.filter((selectedItem) => {
      return selectedItem !== item;
    })

    handleValueChange(_.get(filter, "id", ""), updatedFilters);
  }

  if (!showFilters) {
    return <></>;
  }


  return (
    <div className={styles.dropDownContainer}>
      <Dropdown>
        <Dropdown.Toggle as="div" className={styles.dropDown}>
          select a filter
          <i className='paperflite-dropdown' />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {_.size(dropDownOptions) > 0 && dropDownOptions.map((item, index) => {
            return (
              <Dropdown.Item
                key={index}
                onClick={() => handleOptionSelect(item)}
              >
                {item.value}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>

      <RenderSelectedOptions selectedOptions={selectedOptions} activeCustomField={activeCustomField} handleDeleteClick={handleDeleteClick} />

    </div>
  );
};
export interface IExpiryDate {
  value: any;
  expiryDate: AssetExpiry;
}

const ExpiryDateDropdown: React.FC<PrimaryFilterProps> = ({ filter }) => {
  const { handleExpiryChange } = React.useContext(FilterContext);

  const [selectedFilter, setSelectedFilter] = useState<IExpiryDate>();

  useEffect(() => {
    const selectedDate = { value: _.get(filter, "expiryDateFilter"), expiryDate: _.get(filter, "value[0]") }
    setSelectedFilter(selectedDate);
  }, [filter])

  if (filter.operator === "not_expired" || filter.operator === "expired") {
    return null;
  }

  const handleFilterChange = (item) => {
    const expiryDate = getExpiryDate(item.value, item.expiryDate && item.expiryDate);
    handleExpiryChange(_.get(filter, "id", ""), item.value, [expiryDate]);
  }

  const getExpiryDate = (value: string, expiryDate: Date) => {
    const currentDate = moment();
    document.body.click();

    switch (value) {
      case "one_month":
        return currentDate.add(1, 'months').format('YYYY-MM-DD');
      case "one_week":
        return currentDate.add(1, 'weeks').format('YYYY-MM-DD');
      case "one_day":
        return currentDate.format('YYYY-MM-DD');
      case "custom": {
        const momentValue = moment(expiryDate, 'DD MMMM YYYY');
        return momentValue.format('YYYY-MM-DD');
      }
      default:
        return currentDate.add(1, 'months').format('YYYY-MM-DD');
    }
  };

  const handleDateChange = (date) => {
    const parsedDate = new Date(date);
    const selectedDate = moment(parsedDate).format('DD MMM YYYY'); // formatted to display in ui
    handleFilterChange({ value: "custom", expiryDate: selectedDate });
  };
  return (
    <div className={styles.dropDownContainer}>
      <Dropdown>
        <Dropdown.Toggle as="div" className={styles.dropDown}>
          {_.get(selectedFilter, "expiryDate", "select a filter")}
          <i className='paperflite-dropdown' />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {CONTENT_EXPIRY_FILTERS.map((item, index) => {
            return (
              <Dropdown.Item
                key={index}
                onClick={() => handleFilterChange(item)}
              >
                {item.name}
              </Dropdown.Item>
            );
          })}
          <div className={styles.datepicker}>
            <DatePickerComponent value={selectedFilter?.expiryDate} handleDateChange={handleDateChange} />
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

interface IRenderOptionsProps {
  selectedOptions: any[];
  // eslint-disable-next-line no-unused-vars
  handleDeleteClick: (item: any) => void;
  activeCustomField?: CustomField;
  property?: string;
}

const RenderSelectedOptions: React.FC<IRenderOptionsProps> = ({
  selectedOptions,
  handleDeleteClick,
  activeCustomField,
  property
}) => {
  if (_.size(selectedOptions) === 0) {
    return null;
  }

  return (
    <div className={styles.valueContainer}>
      {_.size(selectedOptions) > 0 && selectedOptions.map((item, index) => {
       let name = _.get(item, "name") || _.get(item, "email") || item;
       let isDisabledUser = false;

       if (activeCustomField?.options) {
         name = _.find(activeCustomField.options, { id: item })?.value || name;
       } else if (property === "user") {
         const fullName = `${_.get(item, "firstName", "")} ${_.get(item, "lastName", "")}`.trim();
         name = fullName || name;
         if (!_.get(item, "enabled", true)) {
          isDisabledUser = true;
         }
       } else if (property === "contact") {
         name = _.get(item, "email", "") || name;
       }
        return <div className={styles.label} key={index}>
          <span className={styles.content}> 
            <div className={styles.contentType}>
              <TruncateWithTooltip
                text={name}
                placement="top"
                className={styles.truncateLabel}
              />
              {isDisabledUser && (
                <span className={styles.deactivatedLabel}>(deactivated)</span>
              )}
            </div>
            <i onClick={() => handleDeleteClick(item)} className="paperflite-close" /> </span>
          {index !== _.size(selectedOptions) - 1 && <span className={styles.or}>or</span>}
        </div >
      })}
    </div>
  );
};

export default InputBox;
